/**
 * useFriendlyNames — COA-10 G2
 * ====================================================
 * Returns a `resolve(id)` function and a `label(id)` shorthand. Internally
 * batches up unknown ids and posts them to GET /api/clinic/people/resolve.
 * Stable across renders, so any number of cells can call it without
 * triggering N×fetches.
 *
 * Usage:
 *   const { label } = useFriendlyNames();
 *   <span>{label(row.assigned_doctor_id)}</span>
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../utils/api';

const SHORT = (id) => {
  if (!id) return '—';
  if (String(id) === 'SYSTEM_POOL') return 'Clinic pool';
  return `#${String(id).slice(0, 8)}`;
};

export function useFriendlyNames() {
  const cacheRef = useRef(new Map());           // id -> {display_name, role_hint}
  const pendingRef = useRef(new Set());          // ids queued for next flush
  const flushTimerRef = useRef(null);
  const [, setTick] = useState(0);

  const flush = useCallback(async () => {
    flushTimerRef.current = null;
    const batch = Array.from(pendingRef.current);
    pendingRef.current = new Set();
    if (batch.length === 0) return;
    try {
      const { data } = await api.get(`/clinic/people/resolve?ids=${encodeURIComponent(batch.join(','))}`);
      if (data?.success && data.items) {
        Object.entries(data.items).forEach(([k, v]) => cacheRef.current.set(String(k), v));
        setTick((t) => t + 1);
      }
    } catch {
      // fail silently — short_id fallback continues to display
    }
  }, []);

  const queue = useCallback((id) => {
    if (!id) return;
    const k = String(id);
    if (cacheRef.current.has(k) || pendingRef.current.has(k)) return;
    pendingRef.current.add(k);
    if (!flushTimerRef.current) {
      flushTimerRef.current = setTimeout(flush, 80);
    }
  }, [flush]);

  useEffect(() => () => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
  }, []);

  const resolve = useCallback((id) => {
    if (!id) return null;
    const k = String(id);
    const v = cacheRef.current.get(k);
    if (v) return v;
    queue(id);
    return null;
  }, [queue]);

  const label = useCallback((id) => {
    if (!id) return '—';
    const v = resolve(id);
    return v?.display_name || SHORT(id);
  }, [resolve]);

  /** Pre-warm cache with a server-side `people` block (escalations/sales-rep
   *  endpoints already return one — saves a roundtrip). */
  const seed = useCallback((people) => {
    if (!people || typeof people !== 'object') return;
    let changed = false;
    Object.entries(people).forEach(([k, v]) => {
      if (!cacheRef.current.has(String(k))) {
        cacheRef.current.set(String(k), v);
        changed = true;
      }
    });
    if (changed) setTick((t) => t + 1);
  }, []);

  return { resolve, label, seed };
}

export default useFriendlyNames;
